import React, { useEffect, useMemo, useState } from 'react';
import {
  useLiveDataStatus,
  aggregateFeedState,
  liveDataStatus,
  type FeedState,
} from '../../lib/liveDataStatus';

/**
 * Track the browser's online/offline state. Firestore's `onSnapshot` does not
 * reliably surface internet drops on its own — the SDK keeps the subscription
 * "alive" and serves cached data while offline. Listening to the browser's own
 * online/offline events is the cheapest way to flag a Wi-Fi/mobile-data drop
 * to the cashier so they don't ring up sales they think are syncing live.
 *
 * Lives in this file (instead of a shared hook) because the badge is the only
 * surface that consumes it today; extract if/when a second consumer appears.
 */
function useNetworkOnline(): boolean {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Re-read once on mount in case the event fired before we attached.
    setOnline(navigator.onLine);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  return online;
}

/**
 * Top-right indicator that shows the health of every long-lived Firestore
 * subscription in the app. Designed to be parked next to the fullscreen
 * toggle in MainLayout (fixed position).
 *
 *   - Green dot         => everything connected
 *   - Spinning amber    => one or more feeds reconnecting (transient)
 *   - Solid red         => one or more feeds offline (retried past the threshold)
 *
 * Clicking the badge opens a small popover listing every degraded feed and
 * a "Refresh all data" button that calls refresh() on every reconnector.
 */

// Human-readable labels for the feed names used by createReconnectingSubscription.
// New feeds added in future can just register with their internal name; if we
// want a prettier label here we add it. Unknown feed names fall back to the raw name.
const FEED_LABELS: Record<string, string> = {
  orders: 'Orders',
  categories: 'Menu categories',
  menu_items: 'Menu items',
  vps_reports_daily: 'Reports API',
  vps_audit_logs: 'Audit API',
};

function feedLabel(name: string): string {
  return FEED_LABELS[name] ?? name;
}

function formatRelative(timestamp: number): string {
  if (!timestamp) return 'never';
  const diff = Date.now() - timestamp;
  if (diff < 1_000) return 'just now';
  if (diff < 60_000) return `${Math.round(diff / 1_000)}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  return `${Math.round(diff / 3_600_000)}h ago`;
}

interface DotProps {
  state: FeedState;
  size?: 'sm' | 'md';
}

const Dot: React.FC<DotProps> = ({ state, size = 'md' }) => {
  const px = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';
  switch (state) {
    case 'connected':
      return <span className={`inline-block ${px} rounded-full bg-emerald-500`} />;
    case 'connecting':
      return <span className={`inline-block ${px} rounded-full bg-amber-400 animate-pulse`} />;
    case 'reconnecting':
      return <span className={`inline-block ${px} rounded-full bg-amber-500 animate-pulse`} />;
    case 'offline':
      return <span className={`inline-block ${px} rounded-full bg-red-500`} />;
    default:
      return <span className={`inline-block ${px} rounded-full bg-gray-400`} />;
  }
};

interface LiveDataStatusBadgeProps {
  /**
   * Where to position the badge. Defaults to fixed in the top-right next to
   * the fullscreen toggle in MainLayout. Pass `inline` to render the chip
   * without absolute positioning (e.g. inside another header).
   */
  variant?: 'fixed' | 'inline';
  /**
   * Compact mode hides the text label and renders just the status dot in a
   * tappable circle. Used in narrow containers (e.g. the collapsed Sidebar
   * footer at 64-80px wide) where the full "Live" pill would overflow and
   * effectively disappear. Tooltip still announces the live status.
   */
  compact?: boolean;
  /**
   * Show the badge even before any feed has registered (shows "Connecting…").
   * Use on Reports when VPS API is enabled so the pill does not flash away.
   */
  showWhenEmpty?: boolean;
}

const LiveDataStatusBadge: React.FC<LiveDataStatusBadgeProps> = ({
  variant = 'fixed',
  compact = false,
  showWhenEmpty = false,
}) => {
  const feeds = useLiveDataStatus();
  const networkOnline = useNetworkOnline();
  const [open, setOpen] = useState(false);

  // If the browser reports no internet, force the badge to "Offline" regardless
  // of Firestore subscription state — those subscriptions silently serve cached
  // data while offline and would otherwise stay green.
  const subscriptionAggregate = useMemo(() => aggregateFeedState(feeds), [feeds]);
  const aggregate: FeedState = !networkOnline ? 'offline' : subscriptionAggregate;

  // Hide until a feed registers (POS/KDS). Reports passes showWhenEmpty for VPS.
  if (feeds.length === 0 && !showWhenEmpty) return null;

  const isDegraded = aggregate !== 'connected';
  const label = (() => {
    if (!networkOnline) return 'No internet';
    switch (aggregate) {
      case 'connected': return 'Live';
      case 'connecting': return 'Connecting…';
      case 'reconnecting': return 'Reconnecting…';
      case 'offline': return 'Offline';
    }
  })();

  // Container needs `position: relative` so the popover anchors to the badge
  // rather than walking up to the viewport (which produced the "popover lands
  // on the right edge of the screen" bug).
  const containerCls = variant === 'fixed'
    ? 'fixed top-4 right-16 z-50'
    : 'relative inline-block';

  const handleRefresh = () => {
    liveDataStatus.refreshAll();
  };

  // Compact mode: just the dot in a small button. Same colour semantics as the
  // full pill so degraded states still pop.
  const buttonCls = compact
    ? 'inline-flex items-center justify-center w-8 h-8 rounded-full shadow transition-colors border ' +
      (isDegraded
        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
        : 'bg-white border-gray-200 hover:bg-gray-50')
    : 'flex items-center gap-2 px-2.5 py-1 rounded-full shadow text-xs font-medium transition-colors ' +
      (isDegraded
        ? 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50');

  return (
    <div className={containerCls}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        // Tooltip stays useful even when popover is closed and is the only
        // affordance for the live status in compact mode (no inline label).
        title={isDegraded ? `${label} — click for details` : 'Live data — click for details'}
        aria-label={isDegraded ? `Live data: ${label}` : 'Live data'}
        className={buttonCls}
      >
        <Dot state={aggregate} />
        {!compact && <span>{label}</span>}
      </button>
      {open && (
        <div
          // Click outside to close: the overlay catches the next click and toggles open=false.
          className="fixed inset-0 z-40"
          onClick={() => setOpen(false)}
        />
      )}
      {open && (
        <div
          // Popover anchoring depends on where the badge lives:
          //   - compact (sidebar collapsed): pops out to the right of the dot
          //     and aligns to its bottom so it grows upward into the page.
          //   - fixed (top-right corner): right-aligned dropdown.
          //   - inline (POSHeader, left side): left-aligned dropdown directly
          //     under the badge so it doesn't fly off-screen to the right.
          className={
            (compact
              ? 'absolute left-full ml-2 bottom-0 '
              : variant === 'fixed'
                ? 'absolute right-0 mt-2 '
                : 'absolute left-0 mt-2 ') +
            'w-72 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs text-gray-800'
          }
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-gray-900">Live data status</div>
            <button
              type="button"
              onClick={handleRefresh}
              className="text-blue-600 hover:text-blue-800 font-medium"
              title="Re-subscribe to every feed now"
            >
              Refresh all
            </button>
          </div>
          {!networkOnline && (
            <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-800">
              <span className="font-semibold">No internet connection.</span>{' '}
              Orders rung up now will sync to the cloud once the connection is back.
            </div>
          )}
          <ul className="space-y-1.5">
            {feeds.map((f) => (
              <li key={f.name} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <Dot state={f.state} size="sm" />
                  <span className="truncate">{feedLabel(f.name)}</span>
                </span>
                <span className="text-gray-500 text-[11px] whitespace-nowrap">
                  {f.state === 'connected'
                    ? `updated ${formatRelative(f.lastSnapshotAt)}`
                    : f.state === 'reconnecting'
                      ? `retry ${(f.retryDelayMs ?? 0) / 1000}s`
                      : f.state === 'offline'
                        ? 'offline'
                        : 'connecting…'}
                </span>
              </li>
            ))}
          </ul>
          {isDegraded && (
            <p className="mt-3 text-[11px] text-gray-500 leading-snug">
              If staff are seeing stale orders or missing menu items, click <strong>Refresh all</strong>
              {' '}or reload the page. New orders rung up will still be saved.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveDataStatusBadge;
